import React, { useState } from "react";
import { X } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";

export default function NewLeadDialog({ onClose, onCreated }) {
  const [f, setF] = useState({
    source_type: "particulier",
    address: "",
    postal_code: "",
    city: "",
    property_category: "huis",
    price: "",
    old_price: "",
    bedrooms: "",
    owner_name: "",
    owner_phone: "",
    agent_name: "",
    agent_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const payload = {
        source_type: f.source_type,
        address: f.address,
        postal_code: f.postal_code,
        city: f.city,
        property_category: f.property_category,
        price: f.price ? Number(f.price) : null,
        old_price: f.old_price ? Number(f.old_price) : null,
        bedrooms: f.bedrooms ? Number(f.bedrooms) : null,
      };
      if (f.source_type === "particulier") {
        payload.owner_name = f.owner_name || null;
        payload.owner_phone = f.owner_phone || null;
      } else {
        payload.agent_name = f.agent_name || null;
        payload.agent_phone = f.agent_phone || null;
      }
      await http.post("/api/leads", payload);
      onCreated();
    } catch (ex) {
      setErr(ex.response?.data?.detail || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(27,36,48,0.45)] backdrop-blur-sm grid place-items-center p-4 fade-in"
         onClick={onClose} data-testid="new-lead-overlay">
      <div className="bg-white rounded-2xl w-full max-w-[560px] shadow-[0_20px_60px_-20px_rgba(14,90,94,0.4)] max-h-[90vh] overflow-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="new-lead-dialog">
        <div className="px-6 pt-5 pb-4 border-b border-[var(--border-soft)] flex items-center justify-between">
          <h3 className="font-display text-[22px] text-[var(--ink-900)]">{t("leads.form.title")}</h3>
          <button type="button" onClick={onClose} data-testid="new-lead-close"
                  className="p-2 rounded-lg hover:bg-[var(--sand-100)]"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <span className="text-[13px] font-medium text-[var(--ink-700)] block mb-2">{t("leads.form.source")}</span>
            <div className="grid grid-cols-2 gap-2">
              {["particulier", "makelaar"].map(s => (
                <button
                  key={s}
                  type="button"
                  data-testid={`new-lead-source-${s}`}
                  onClick={() => set("source_type", s)}
                  className={`btn ${f.source_type === s ? "btn-primary" : "btn-ghost"} h-10 justify-center`}
                >
                  {t(`leads.form.${s}`)}
                </button>
              ))}
            </div>
          </div>

          <Field label={t("leads.form.address")}>
            <input required value={f.address} onChange={(e) => set("address", e.target.value)}
                   data-testid="new-lead-address" className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("leads.form.postal_code")}>
              <input required value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)}
                     data-testid="new-lead-postal" className={inputCls} />
            </Field>
            <Field label={t("leads.form.city")}>
              <input required value={f.city} onChange={(e) => set("city", e.target.value)}
                     data-testid="new-lead-city" className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("leads.form.category")}>
              <select value={f.property_category} onChange={(e) => set("property_category", e.target.value)}
                      data-testid="new-lead-category" className={inputCls}>
                <option value="huis">{t("leads.form.huis")}</option>
                <option value="appartement">{t("leads.form.appartement")}</option>
              </select>
            </Field>
            <Field label={t("leads.form.bedrooms")}>
              <input type="number" min="0" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}
                     data-testid="new-lead-bedrooms" className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("leads.form.price")}>
              <input type="number" min="0" value={f.price} onChange={(e) => set("price", e.target.value)}
                     data-testid="new-lead-price" className={inputCls} />
            </Field>
            <Field label={t("leads.form.old_price")}>
              <input type="number" min="0" value={f.old_price} onChange={(e) => set("old_price", e.target.value)}
                     data-testid="new-lead-oldprice" className={inputCls} />
            </Field>
          </div>

          {f.source_type === "particulier" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("leads.form.contact_name")}>
                <input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)}
                       data-testid="new-lead-owner-name" className={inputCls} />
              </Field>
              <Field label={t("leads.form.contact_phone")}>
                <input value={f.owner_phone} onChange={(e) => set("owner_phone", e.target.value)}
                       data-testid="new-lead-owner-phone" className={inputCls} />
              </Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("leads.form.contact_name")}>
                <input value={f.agent_name} onChange={(e) => set("agent_name", e.target.value)}
                       data-testid="new-lead-agent-name" className={inputCls} />
              </Field>
              <Field label={t("leads.form.contact_phone")}>
                <input value={f.agent_phone} onChange={(e) => set("agent_phone", e.target.value)}
                       data-testid="new-lead-agent-phone" className={inputCls} />
              </Field>
            </div>
          )}

          {err && <div className="text-sm text-[var(--danger)] bg-[#FBE4E4] rounded-lg px-3 py-2">{String(err)}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">{t("common.cancel")}</button>
            <button type="submit" disabled={busy} data-testid="new-lead-submit"
                    className="btn btn-primary disabled:opacity-60">
              {busy ? t("common.loading") : t("leads.form.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "mt-1 w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-[var(--ink-700)]">{label}</span>
      {children}
    </label>
  );
}
